/**
 * Chart and Number Formatting Utilities
 * Industry-standard formatting for financial displays
 */

/**
 * Format large numbers for chart axes (industry standard)
 * @param {number} value - The numeric value to format
 * @param {string} currencySymbol - Currency symbol (e.g., '$', '£')
 * @returns {string} Formatted string (e.g., '5m', '80k', '500')
 *
 * Examples:
 * - 5,000,000 → "5.0m"
 * - 3,200,000 → "3.2m"
 * - 500,000 → "500k"
 * - 80,000 → "80k"
 * - 999 → "999"
 */
export const formatChartValue = (value, currencySymbol = '$') => {
  const absValue = Math.abs(value);

  if (absValue >= 1000000) {
    // Use 'm' for millions (e.g., 5.0m, 3.2m)
    return `${currencySymbol}${(value / 1000000).toFixed(1)}m`;
  } else if (absValue >= 1000) {
    // Use 'k' for thousands (e.g., 80k, 500k)
    return `${currencySymbol}${(value / 1000).toFixed(0)}k`;
  } else {
    // Show actual number for values under 1000
    return `${currencySymbol}${value.toFixed(0)}`;
  }
};

/**
 * Format currency values for text display (no decimals)
 * @param {number} value - The numeric value to format
 * @param {string} currencySymbol - Currency symbol (e.g., '$', '£')
 * @returns {string} Formatted string (e.g., '£10,096,807')
 *
 * Examples:
 * - 10096807.487 → "£10,096,807" (not "£10,096,807.487")
 * - 500000.99 → "$500,000" (not "$500,000.99")
 */
export const formatCurrencyText = (value, currencySymbol = '$') => {
  return `${currencySymbol}${value.toLocaleString(undefined, {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  })}`;
};

/**
 * Format percentages for display
 * @param {number} value - The percentage value (e.g., 0.75 for 75%)
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 1) => {
  return `${(value * 100).toFixed(decimals)}%`;
};
